import { Component, OnInit } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { UrlSignature } from '../common/apiEndpoint';
import { SuperloginService } from '../superlogin/superlogin.service';


interface Applicant {
  id: number;
  firstname: string;
  lastname: string;
  email: string;
  phoneNumber: string;
  amount: number;
  referenceID: string;
  paymentStatus: string;
  pin: string;
  checkoutId: string;
  otpCode: string;
  typeofStudent: string;
  otpExpiresAt: string;
  createdAt: string;
  updatedAt: string;
  resetToken: string | null;
  resetTokenExpiresAt: string | null;
  verified: boolean;
}

interface ApiResponse {
  month: number;
  year: number;
  monthName: string;
  count: number;
  success: boolean;
  data: Applicant[];
}

@Component({
  selector: 'app-newsignups',
  templateUrl: './newsignups.component.html',
  styleUrls: ['./newsignups.component.css']
})
export class NewsignupsComponent implements OnInit {

  allApplicants: Applicant[] = [];
  filteredApplicants: Applicant[] = [];
  pagedApplicants: Applicant[] = [];

  isLoading = false;
  errorMessage = '';
  monthName = '';
  responseYear: number = new Date().getFullYear();

  searchQuery = '';
  activeFilter = 'ALL';

  currentPage = 1;
  pageSize = 10;
  totalPages = 1;
  pages: number[] = [];

  expandedRowId: number | null = null;

toggleRow(id: number): void {
  this.expandedRowId = this.expandedRowId === id ? null : id;
}

// Update constructor
constructor(
  private http: HttpClient,
  private urls: UrlSignature,
  private authService: SuperloginService
) {}
  ngOnInit(): void {
    this.loadData();
  }

  // Replace getHeaders() entirely
getHeaders(): HttpHeaders {
  const token = this.authService.getToken() || '';
  return new HttpHeaders({
    'Authorization': 'Bearer ' + token,
    'Content-Type': 'application/json'
  });
}

  loadData(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.http.get<ApiResponse>(this.urls.CurrentMonthApplicantUrl, { headers: this.getHeaders() })
      .subscribe({
        next: (res) => {
          if (!res.success) {
            this.errorMessage = 'Server returned an unsuccessful response.';
            this.isLoading = false;
            return;
          }
          this.allApplicants = res.data || [];
          this.monthName = res.monthName
            ? res.monthName.charAt(0) + res.monthName.slice(1).toLowerCase()
            : '';
          this.responseYear = res.year || new Date().getFullYear();
          this.isLoading = false;
          this.applyFilters();
        },
        error: (err) => {
          this.errorMessage = err?.error?.message || 'Failed to load applicants. Please try again.';
          this.isLoading = false;
        }
      });
  }

  setFilter(filter: string): void {
    this.activeFilter = filter;
    this.currentPage = 1;
    this.applyFilters();
  }

  onSearch(): void {
    this.currentPage = 1;
    this.applyFilters();
  }

  applyFilters(): void {
    const q = this.searchQuery.toLowerCase().trim();
    this.filteredApplicants = this.allApplicants.filter(a => {
      const matchFilter =
        this.activeFilter === 'ALL' ||
        (this.activeFilter === 'PAID' && a.paymentStatus === 'PAID') ||
        (this.activeFilter === 'PENDING' && a.paymentStatus !== 'PAID') ||
        (this.activeFilter === 'INTERNATIONAL' && a.typeofStudent === 'INTERNATIONAL') ||
        (this.activeFilter === 'GHANAIAN' && a.typeofStudent === 'GHANAIAN');

      const fullName = (a.firstname + ' ' + a.lastname).toLowerCase();
      const matchSearch = !q ||
        fullName.includes(q) ||
        (a.email || '').toLowerCase().includes(q) ||
        (a.referenceID || '').toLowerCase().includes(q);

      return matchFilter && matchSearch;
    });
    this.buildPagination();
  }

  buildPagination(): void {
    this.totalPages = Math.max(1, Math.ceil(this.filteredApplicants.length / this.pageSize));
    if (this.currentPage > this.totalPages) this.currentPage = 1;
    this.pages = Array.from({ length: this.totalPages }, (_, i) => i + 1);
    this.slicePage();
  }

  slicePage(): void {
    const start = (this.currentPage - 1) * this.pageSize;
    this.pagedApplicants = this.filteredApplicants.slice(start, start + this.pageSize);
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
    this.slicePage();
  }

  get statPaid(): number { return this.allApplicants.filter(a => a.paymentStatus === 'PAID').length; }
  get statPending(): number { return this.allApplicants.filter(a => a.paymentStatus !== 'PAID').length; }
  get statVerified(): number { return this.allApplicants.filter(a => a.verified).length; }
  get statInternational(): number { return this.allApplicants.filter(a => a.typeofStudent === 'INTERNATIONAL').length; }
  get statGhanaian(): number { return this.allApplicants.filter(a => a.typeofStudent === 'GHANAIAN').length; }

  getInitials(a: Applicant): string {
    return ((a.firstname || '?')[0] + (a.lastname || '?')[0]).toUpperCase();
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  get pageStart(): number { return (this.currentPage - 1) * this.pageSize + 1; }
  get pageEnd(): number { return Math.min(this.currentPage * this.pageSize, this.filteredApplicants.length); }
}